import React from 'react';
import { useCalculatorStore } from '../../store/calculatorStore';
import { Input } from '../ui/Input';

interface MonthlyExpensesSectionProps {
  tabId: string;
}

export const MonthlyExpensesSection: React.FC<MonthlyExpensesSectionProps> = ({ tabId }) => {
  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);
  const updateCalculatorTabData = useCalculatorStore(s => s.updateCalculatorTabData);
  const tab = calculatorTabs.find(t => t.id === tabId);

  if (!tab) return <div className="p-4 text-center text-ink-muted">Calculator not found</div>;

  const expenses = tab.data.annualExpenses;
  const mortgageTerms = tab.data.mortgageTerms;

  const showPmi =
    mortgageTerms.country === 'USA' &&
    mortgageTerms.loanProgram === 'conventional' &&
    mortgageTerms.downPaymentPercentage < 20;

  const handleChange = (
    field: keyof typeof expenses,
    value: { type: 'percent' | 'amount'; value: number }
  ) => {
    updateCalculatorTabData(tabId, {
      annualExpenses: { ...expenses, [field]: value },
    });
  };

  const handlePmiChange = (pmi: { type: 'percent' | 'amount'; value: number }) => {
    updateCalculatorTabData(tabId, {
      mortgageTerms: { ...mortgageTerms, pmi },
    });
  };

  return (
    <div className="px-5 pb-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
            Property Taxes
          </label>
          <Input
            label=""
            type="number"
            value={expenses.propertyTaxes.value}
            onChange={e =>
              handleChange('propertyTaxes', {
                type: expenses.propertyTaxes.type,
                value: Number(e.target.value),
              })
            }
            prefix={expenses.propertyTaxes.type === 'amount' ? '$' : ''}
            suffix={expenses.propertyTaxes.type === 'percent' ? '%' : ''}
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
            Home Insurance
          </label>
          <Input
            label=""
            type="number"
            value={expenses.homeInsurance.value}
            onChange={e =>
              handleChange('homeInsurance', {
                type: expenses.homeInsurance.type,
                value: Number(e.target.value),
              })
            }
            prefix={expenses.homeInsurance.type === 'amount' ? '$' : ''}
            suffix={expenses.homeInsurance.type === 'percent' ? '%' : ''}
            formatWithCommas={expenses.homeInsurance.type === 'amount'}
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
            HOA / Utilities
          </label>
          <Input
            label=""
            type="number"
            value={expenses.hoaUtilities.value}
            onChange={e =>
              handleChange('hoaUtilities', {
                type: expenses.hoaUtilities.type,
                value: Number(e.target.value),
              })
            }
            prefix={expenses.hoaUtilities.type === 'amount' ? '$' : ''}
            suffix={expenses.hoaUtilities.type === 'percent' ? '%' : ''}
            formatWithCommas={expenses.hoaUtilities.type === 'amount'}
          />
        </div>

        {/* PMI - USA conventional only, < 20% down */}
        {showPmi && (
          <div className="md:col-span-3">
            <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
              PMI (Private Mortgage Insurance)
            </label>
            <div className="flex gap-2 items-center">
              <div className="flex-1 max-w-[200px]">
                <Input
                  label=""
                  type="number"
                  value={mortgageTerms.pmi.value}
                  onChange={e =>
                    handlePmiChange({
                      type: mortgageTerms.pmi.type,
                      value: Number(e.target.value),
                    })
                  }
                  prefix={mortgageTerms.pmi.type === 'amount' ? '$' : ''}
                  suffix={mortgageTerms.pmi.type === 'percent' ? '%' : ''}
                  step={mortgageTerms.pmi.type === 'percent' ? 0.1 : 10}
                />
              </div>
              <div className="flex rounded-lg bg-stone border border-stone-border overflow-hidden shrink-0">
                <button
                  onClick={() => handlePmiChange({ type: 'percent', value: mortgageTerms.pmi.value })}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    mortgageTerms.pmi.type === 'percent'
                      ? 'bg-navy-800 text-white'
                      : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  %/yr
                </button>
                <button
                  onClick={() => handlePmiChange({ type: 'amount', value: mortgageTerms.pmi.value })}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    mortgageTerms.pmi.type === 'amount'
                      ? 'bg-navy-800 text-white'
                      : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  $/mo
                </button>
              </div>
              <p className="text-[10px] text-ink-faint">Cancels automatically when balance drops to 80% LTV</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
